/**
 * WebSocket context for global WebSocket connection.
 * 
 * Manages a single WebSocket connection for the entire application.
 * Connection is established when user is authenticated and destroyed on logout.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../config/runtime';
import { logError } from '../utils/errorLogging';

interface WebSocketMessage {
  event: string;
  data?: any;
  timestamp?: string;
}

type WebSocketEventHandler = (message: WebSocketMessage) => void;

interface WebSocketContextType {
  isConnected: boolean;
  sendMessage: (event: string, data?: any) => boolean;
  on: (event: string, handler: WebSocketEventHandler) => () => void;
  off: (event: string, handler: WebSocketEventHandler) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const eventHandlersRef = useRef<Map<string, Set<WebSocketEventHandler>>>(new Map());
  const shouldConnectRef = useRef(false);
  const maxReconnectAttempts = 30; // 30 seconds with 1 second intervals
  const reconnectInterval = 1000; // 1 second

  // Get WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    try {
      const apiUrl = getApiUrl();
      // Handle case where getApiUrl() might return undefined (e.g., in tests)
      if (!apiUrl) {
        // Fallback to current window location if available
        if (typeof window !== 'undefined' && window.location) {
          const origin = window.location.origin;
          const protocol = origin.startsWith('https') ? 'wss' : 'ws';
          return `${origin.replace(/^https?/, protocol)}/ws`;
        }
        // Last resort fallback
        return 'ws://localhost:8000/ws';
      }
      let wsUrl = apiUrl.replace(/^http/, 'ws');
      if (apiUrl.startsWith('https')) {
        wsUrl = apiUrl.replace(/^https/, 'wss');
      }
      return wsUrl + '/ws';
    } catch (error) {
      // Fallback if getApiUrl() throws (e.g., in test environment)
      if (typeof window !== 'undefined' && window.location) {
        const origin = window.location.origin;
        const protocol = origin.startsWith('https') ? 'wss' : 'ws';
        return `${origin.replace(/^https?/, protocol)}/ws`;
      }
      return 'ws://localhost:8000/ws';
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (isConnectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) {
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (!shouldConnectRef.current || !user) {
      return;
    }

    isConnectingRef.current = true;

    // Clean up existing connection
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        // Ignore errors during cleanup
      }
      wsRef.current = null;
    }

    try {
      const wsUrl = getWebSocketUrl();
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle ping/pong
          if (message.event === 'ping') {
            sendMessage('pong');
            return;
          }

          // Call all registered handlers for this event
          const handlers = eventHandlersRef.current.get(message.event);
          if (handlers) {
            handlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                logError(`Error in WebSocket event handler for "${message.event}"`, error);
              }
            });
          }

          // Also call handlers registered for 'all' to receive all messages
          const allHandlers = eventHandlersRef.current.get('*');
          if (allHandlers) {
            allHandlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                logError('Error in WebSocket "*" event handler', error);
              }
            });
          }
        } catch (error) {
          logError('Failed to parse WebSocket message', error);
        }
      };

      ws.onerror = (error) => {
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        setIsConnected(false);

        // Handle authentication failure (code 1008)
        if (event.code === 1008) {
          logError('WebSocket authentication failed, logging out');
          shouldConnectRef.current = false;
          logout().then(() => {
            window.location.href = '/login';
          });
          return;
        }

        // Attempt reconnection if we should still be connected
        if (shouldConnectRef.current && user && event.code !== 1000) {
          reconnectAttemptsRef.current += 1;

          // If we've exceeded max attempts, logout and redirect
          if (reconnectAttemptsRef.current > maxReconnectAttempts) {
            logError('WebSocket failed to reconnect after 30 seconds, logging out');
            shouldConnectRef.current = false;
            logout().then(() => {
              window.location.href = '/login';
            });
            return;
          }

          // Schedule reconnection
          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldConnectRef.current && user) {
              connect();
            }
          }, reconnectInterval);
        }
      };
    } catch (error) {
      logError('Failed to create WebSocket connection', error);
      isConnectingRef.current = false;
    }
  }, [user, logout, getWebSocketUrl]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    shouldConnectRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Send message
  const sendMessage = useCallback((event: string, data?: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ event, data }));
        return true;
      } catch (error) {
        logError('Failed to send WebSocket message', error);
        return false;
      }
    }
    return false;
  }, []);

  // Register event handler
  const on = useCallback((event: string, handler: WebSocketEventHandler) => {
    if (!eventHandlersRef.current.has(event)) {
      eventHandlersRef.current.set(event, new Set());
    }
    eventHandlersRef.current.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      off(event, handler);
    };
  }, []);

  // Unregister event handler
  const off = useCallback((event: string, handler: WebSocketEventHandler) => {
    const handlers = eventHandlersRef.current.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        eventHandlersRef.current.delete(event);
      }
    }
  }, []);

  // Connect when user is authenticated, disconnect on logout
  useEffect(() => {
    // Only connect if user is authenticated and not on login page
    if (user && window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
      shouldConnectRef.current = true;
      connect();
    } else {
      shouldConnectRef.current = false;
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);

  // Disconnect on logout
  useEffect(() => {
    if (!user) {
      disconnect();
    }
  }, [user, disconnect]);

  const value: WebSocketContextType = {
    isConnected,
    sendMessage,
    on,
    off,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

